import { UeElement } from '@oneaudi/falcon-tools';
import React, { useContext, useEffect } from 'react';
import { Context } from '../../../context/Context';
import { useTrackingManager } from '../../../utils/useTrackingManager';

import { handleSubmenuFocus, trapFocusWithDelay } from '../../../utils/a11y';
import {
  StyledHeadline,
  SubmenuTriggerButton,
  SubmenuTriggerWrapper,
} from '../SubPageNavigationStyles';
import { ArrowIcon } from './ArrowIcon';
import SubmenuTriggerHeadline from './SubmenuTriggerHeadline';

const SubmenuTrigger: React.FC = () => {
  const { navigation } = useTrackingManager();
  const {
    headline,
    setPopOverState,
    hasLinks,
    hasCta,
    submenuDesktopRef,
    submenuMobileRef,
    submenuTriggerRef,
    showSubmenu,
    toggleShowSubmenu,
    triggerId,
    submenuId,
  } = useContext(Context);

  const handleNavClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setPopOverState(false);

    const mobileDevice = window.innerWidth < 1024;

    toggleShowSubmenu();

    const submenuRef = mobileDevice ? submenuMobileRef.current : submenuDesktopRef.current;
    const triggerRef = submenuTriggerRef.current;

    if (!submenuRef || !triggerRef) return;

    if (!showSubmenu) {
      trapFocusWithDelay(submenuRef, triggerRef, 100);
    }
    navigation(showSubmenu);
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    const mobileDevice = window.innerWidth < 1024;
    if (event.key === 'Escape' && showSubmenu) {
      setPopOverState(false);
      if (
        (submenuDesktopRef?.current?.contains(document.activeElement) ||
          submenuMobileRef?.current?.contains(document.activeElement)) &&
        submenuTriggerRef.current
      ) {
        event.preventDefault();
        submenuTriggerRef.current.focus();
      }
      toggleShowSubmenu();
    }

    if (
      (event.key === 'Enter' || event.key === 'ArrowDown' || event.key === ' ') &&
      event.target === submenuTriggerRef.current
    ) {
      event.preventDefault();
      if (mobileDevice) {
        // Handle mobile submenu
        handleSubmenuFocus(
          submenuMobileRef,
          submenuTriggerRef,
          showSubmenu,
          toggleShowSubmenu,
          100,
        );
      } else {
        // Handle desktop submenu
        handleSubmenuFocus(
          submenuDesktopRef,
          submenuTriggerRef,
          showSubmenu,
          toggleShowSubmenu,
          30,
        );
      }
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showSubmenu]);

  return (
    <SubmenuTriggerWrapper>
      <SubmenuTriggerButton
        onClick={(e) => handleNavClick(e)}
        ref={submenuTriggerRef}
        aria-haspopup="menu"
        aria-expanded={showSubmenu}
        aria-controls={submenuId}
        id={triggerId}
        aria-labelledby={`headline${hasLinks ? ' subheadline' : ''}`}
      >
        <SubmenuTriggerHeadline />
        {(hasLinks || hasCta) && <ArrowIcon rotated={showSubmenu} />}
      </SubmenuTriggerButton>
      <StyledHeadline
        variant={{ order: '2', style: 'bold' }}
        className="headline"
        role="presentation"
      >
        <UeElement type="text" property="headline" label="Main Page Title">
          {headline}
        </UeElement>
      </StyledHeadline>
    </SubmenuTriggerWrapper>
  );
};

export default SubmenuTrigger;
